import React, { useEffect, useRef } from "react"
import styled, { keyframes } from "styled-components"
import { themes } from "../styles/ColorStyles"

const nm = themes.neumorphic

// ── Constants ────────────────────────────────────────────────
const ROW_H        = 56   // px — fixed height per row (fits 1-2 lines at 12px)
const ROWS_VISIBLE = 4    // always show 4 rows

// ── Rich-text helpers ────────────────────────────────────────
const b = t => ({ bold: true, t })

const renderNodes = nodes =>
  nodes.map((n, i) =>
    typeof n === "string" ? n : <strong key={i}>{n.t}</strong>
  )

// ── Data (newest first) ──────────────────────────────────────
const NEWS = [
  { date: "Jun 2024", nodes: ["Joined ", b("Adobe Research"), " as a ", b("Research Scientist"), "."] },
  { date: "Feb 2025", nodes: ["Three papers accepted at ", b("CVPR 2025"), ". Huge congrats to ", b("Shaoteng"), " & ", b("Xin Yu"), " — my very first interns! 🎉"] },
  { date: "Oct 2025", nodes: ["My led project ", b("GenProp"), " was selected for ", b("Adobe MAX Sneak 2025"), ", featured as ", b("Project Frame Forward"), "."] },
  { date: "Jan 2026", nodes: ["Promoted to ", b("Research Scientist II"), "."] },
  { date: "Feb 2026", nodes: [b("EditVerse"), " accepted as an ", b("Oral"), " at ", b("ICLR 2026"), ". Congrats to our intern ", b("Xuan Ju"), " — well deserved! 🎊"] },
  { date: "Feb 2026", nodes: ["Three papers accepted at ", b("CVPR 2026"), ". Congrats to my interns ", b("Xin Yu"), ", ", b("Gengze"), " & ", b("Qiucheng"), "! 🎉"] },
  { date: "Jul 2026", nodes: [b("Chimera"), " is out on arXiv — a hybrid visual diffusion backbone with ", b("linear attention"), " and Chinchilla-style ", b("scaling laws"), ", reaching ", b("7.3×"), " the compute efficiency of full attention."] },
]

// ── Styled components ────────────────────────────────────────
const blinkBlock = keyframes`50% { opacity: 0; }`

const Wrapper = styled.section`
  background: ${nm.bg};
  padding: 28px 0 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Inner = styled.div`
  width: 100%;
  max-width: 780px;
  padding: 0 32px;
  @media (max-width: 600px) { padding: 0 20px; }
`

const SectionTitle = styled.div`
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  letter-spacing: 0.12em;
  font-weight: 700;
  color: ${nm.text1};
  margin-bottom: 16px;
  .prefix { color: ${nm.accent}; margin-right: 6px; }
`

const LogBox = styled.div`
  background: ${nm.bg};
  border-radius: 16px;
  padding: 20px 28px 20px;
  box-shadow: ${nm.shadowLarge};
  @media (max-width: 600px) { padding: 16px 16px; }
`

const LogPrompt = styled.div`
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  color: ${nm.text2};
  margin-bottom: 12px;
  user-select: none;
  .ps { color: ${nm.accent}; font-weight: 700; margin-right: 6px; }
`

/* Fixed-height viewport: clips to 4 rows, user can scroll */
const RowViewport = styled.div`
  height: ${ROW_H * ROWS_VISIBLE}px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: ${nm.accent}40 transparent;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: ${nm.accent}60; border-radius: 2px; }
`

/* Inner list: all rows, auto-scroll controlled by JS */
const RowList = styled.div`
  position: relative;
`

const LogRow = styled.div`
  min-height: ${ROW_H}px;
  padding: 6px 0;
  display: flex;
  align-items: center;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  line-height: 1.45;
`

const LogDate = styled.span`
  flex-shrink: 0;
  color: ${nm.accent};
  font-weight: 700;
  background: ${nm.accentLight};
  border-radius: 5px;
  padding: 1px 6px;
  font-size: 10.5px;
  letter-spacing: 0.04em;
  margin-right: 10px;
  white-space: nowrap;
`

const LogDash = styled.span`
  color: ${nm.text3};
  margin: 0 7px;
  flex-shrink: 0;
  user-select: none;
`

const LogText = styled.span`
  color: ${nm.text2};
  flex: 1;
  min-width: 0;

  strong {
    color: #1a1a2e;
    font-weight: 900;
    letter-spacing: 0.01em;
  }
`

const CursorRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  color: ${nm.text3};
`

const Cursor = styled.span`
  display: inline-block;
  width: 8px; height: 14px;
  background: ${nm.accent};
  margin-left: 4px;
  border-radius: 1px;
  vertical-align: middle;
  opacity: 0.6;
  animation: ${blinkBlock} 1s step-end infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`

// ── Component ────────────────────────────────────────────────

const NewsSection = () => {
  const viewportRef = useRef(null)
  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = viewportRef.current.scrollHeight
  }, [])

  // ── Render each row ─────────────────────────────────────────
  const renderRow = (item, idx) => {

    return (
      <LogRow key={idx}>
        <LogDate>[{item.date}]</LogDate>
        <LogDash>—</LogDash>
        <LogText>
          {renderNodes(item.nodes)}
        </LogText>
      </LogRow>
    )
  }

  return (
    <Wrapper id="news">
      <Inner>
        <SectionTitle>
          <span className="prefix">//</span>NEWS.LOG
        </SectionTitle>
        <LogBox>
          <LogPrompt>
            <span className="ps">$</span>cat news.log
          </LogPrompt>

          <RowViewport ref={viewportRef}>
            <RowList>
              {NEWS.map((item, idx) => renderRow(item, idx))}
            </RowList>
          </RowViewport>

          <CursorRow>$&nbsp;<Cursor /></CursorRow>
        </LogBox>
      </Inner>
    </Wrapper>
  )
}

export default NewsSection
